import React from 'react'
import { useTranslation } from 'react-i18next'

interface LegendItem {
    label: string
    highlight?: boolean
    strikethrough?: boolean
}

interface LegendCardProps {
    title: string
    borderColor: string
    items: LegendItem[]
    example?: string
}

const LegendCard: React.FC<LegendCardProps> = ({
    title,
    borderColor,
    items,
    example,
}) => {
    const { t } = useTranslation('marketing')

    return (
        <div
            className="bg-white rounded p-3 text-xs"
            style={{ borderLeft: `3px solid ${borderColor}` }}
        >
            <div className="font-semibold text-gray-800 mb-2">{title}</div>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
                {items.map((item, idx) => (
                    <li key={idx}>
                        {item.strikethrough ? (
                            <span className="text-red-500 line-through italic">
                                {item.label}
                            </span>
                        ) : item.highlight ? (
                            <span className="text-red-500">{item.label}</span>
                        ) : (
                            item.label
                        )}
                    </li>
                ))}
            </ul>
            {example && (
                <div className="mt-2 text-gray-500 italic text-[10px]">
                    {t('lead_flow.example')}: {example}
                </div>
            )}
        </div>
    )
}

const LeadStatusLegend: React.FC<{ className?: string }> = ({
    className = '',
}) => {
    const { t } = useTranslation('marketing')

    return (
        <div className="p-2">
            <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${className}`}
            >
                <LegendCard
                    title={t('lead_flow.legend.warm_lead')}
                    borderColor="#F59E0B"
                    items={[
                        { label: t('lead_flow.legend.authority_yes') },
                        {
                            label: t('lead_flow.legend.need_interested'),
                            highlight: true,
                        },
                    ]}
                    example={t('lead_flow.legend.warm_lead_example')}
                />
                <LegendCard
                    title={t('lead_flow.legend.cool_lead')}
                    borderColor="#D1D5DB"
                    items={[
                        { label: t('lead_flow.legend.budget_yes') },
                        { label: t('lead_flow.legend.authority_yes') },
                        {
                            label: t('lead_flow.legend.need_not_yet'),
                            highlight: true,
                        },
                    ]}
                    example={t('lead_flow.legend.cool_lead_example')}
                />
                <LegendCard
                    title={t('lead_flow.legend.sql')}
                    borderColor="#22C55E"
                    items={[
                        { label: t('lead_flow.legend.budget_yes') },
                        { label: t('lead_flow.legend.authority_yes') },
                        {
                            label: t('lead_flow.legend.need_has'),
                            highlight: true,
                        },
                        { label: t('lead_flow.legend.timeline_has') },
                    ]}
                    example={t('lead_flow.legend.sql_example')}
                />
                <LegendCard
                    title={t('lead_flow.legend.unqualified')}
                    borderColor="#F87171"
                    items={[
                        {
                            label: t('lead_flow.legend.budget_no'),
                            highlight: true,
                        },
                        {
                            label: t('lead_flow.legend.authority_no'),
                            highlight: true,
                        },
                        {
                            label: t('lead_flow.legend.need_no'),
                            highlight: true,
                        },
                    ]}
                    example={t('lead_flow.legend.unqualified_example')}
                />
            </div>
        </div>
    )
}

export default LeadStatusLegend
